import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { EntitlementService } from '@cosmos/angular/utils';
import { ElementIntent, FEATURE_ID, ModuleKey } from '@cosmos/shared';

export type TierButtonSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'cosmos-tier-button',
  templateUrl: './tier-button.component.html',
  styleUrl: './tier-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslatePipe, TooltipModule],
})
export class TierButtonComponent {
  private readonly entitlementService = inject(EntitlementService);

  readonly actionText = input.required<string>();
  readonly module = input<ModuleKey>('project');
  readonly feature = input<FEATURE_ID>(null);
  readonly size = input<TierButtonSize>('md');

  /**
   * What this button does. Determines behaviour under locked/inactive state.
   * Defaults to 'mutate' since tier buttons are always write/premium actions.
   */
  readonly intent = input<ElementIntent>('mutate');

  readonly clicked = output<void>();

  /**
   * Full decision tree — project/module state first (cheapest), then
   * feature/quota. Returns a translation key string when locked, null when ok.
   */
  private readonly _lockReason = computed<string | null>(() => {
    const module = this.module();
    const intent = this.intent();
    const projectState = this.entitlementService.projectAccessState(module)();

    // Step 1: project inactive — treat same as locked for tier button display
    // (caller should @if away mutate buttons; this covers edge cases)
    if (projectState === 'inactive' && (intent === 'mutate' || intent === 'explore')) {
      return 'access.project-inactive';
    }

    // Step 2: module locked
    if (projectState === 'locked') {
      return 'access.module-locked';
    }

    const feature = this.feature();

    // Step 3: feature tier gate
    if (feature && !this.entitlementService.hasFeature(module, feature)) {
      return 'access.feature-unavailable';
    }

    // Step 4: quota exhausted
    if (feature) {
      const bucket = this.entitlementService.getBucket(module, feature as any);
      if (bucket && !bucket.allowNegative && bucket.remaining <= 0) {
        return 'access.quota-exhausted';
      }
    }

    // Legacy fallback: no feature declared — check module-level access
    if (!feature) {
      const access = this.entitlementService.moduleAccess()[module];
      if (!access || access.mode === 'preview' || access.status === 'locked') {
        return 'tiers.locked-tooltip';
      }
    }

    return null;
  });

  readonly isLocked = computed(() => this._lockReason() !== null);

  readonly hostClasses = computed(() => ({
    'tier-btn': true,
    [`tier-btn--${this.size()}`]: true,
    'tier-btn--pro': true,
    'tier-btn--locked': this.isLocked(),
  }));

  readonly tooltipText = computed(() => this._lockReason() ?? '');

  onClick(): void {
    if (!this.isLocked()) {
      this.clicked.emit();
    }
  }
}
